import { CardChoice, GameEvent } from "../engine/types";
import { EventListener, Transport } from "./transport";

export interface OnlineClientOpts {
  gameURL: string;
  gameId: string;
  token: string;
  spectate: boolean;
}

export class OnlineClient implements Transport {
  spectate: boolean;
  private url: string;
  private ws: WebSocket | null = null;
  private listeners = new Set<EventListener>();
  private stopped = false;
  private reconnectMs = 1000;
  private maxReconnectMs = 30000;
  private outbox: CardChoice[] = [];

  constructor(opts: OnlineClientOpts) {
    this.spectate = opts.spectate;
    const role = opts.spectate ? "spectate" : "play";
    const base = opts.gameURL.replace(/\/$/, "");
    this.url = `${base}/online/${role}/${opts.gameId}?token=${encodeURIComponent(
      opts.token
    )}`;
  }

  start(): void {
    this.connect();
  }

  stop(): void {
    this.stopped = true;
    if (this.ws) {
      try {
        this.ws.close();
      } catch {}
      this.ws = null;
    }
  }

  onEvent(l: EventListener): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }

  send(choice: CardChoice): void {
    if (this.spectate) return;
    const payload = JSON.stringify(choice);
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(payload);
    } else {
      this.outbox.push(choice);
    }
  }

  private flushOutbox(): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    while (this.outbox.length > 0) {
      const c = this.outbox.shift()!;
      this.ws.send(JSON.stringify(c));
    }
  }

  private connect(): void {
    if (this.stopped) return;
    try {
      this.ws = new WebSocket(this.url);
    } catch (e) {
      this.scheduleReconnect();
      return;
    }
    this.ws.binaryType = "blob";
    this.ws.onopen = () => {
      this.reconnectMs = 1000;
      this.flushOutbox();
    };
    this.ws.onmessage = async (ev) => {
      let text: string;
      if (typeof ev.data === "string") {
        text = ev.data;
      } else if (ev.data instanceof Blob) {
        text = await ev.data.text();
      } else if (ev.data instanceof ArrayBuffer) {
        text = new TextDecoder().decode(new Uint8Array(ev.data));
      } else {
        return;
      }
      let parsed: GameEvent | null = null;
      try {
        parsed = JSON.parse(text) as GameEvent;
      } catch {
        return;
      }
      if (!parsed) return;
      this.listeners.forEach((l) => l(parsed!));
    };
    this.ws.onerror = () => {};
    this.ws.onclose = () => {
      this.ws = null;
      this.scheduleReconnect();
    };
  }

  private scheduleReconnect(): void {
    if (this.stopped) return;
    const delay = this.reconnectMs;
    this.reconnectMs = Math.min(this.reconnectMs * 2, this.maxReconnectMs);
    setTimeout(() => this.connect(), delay);
  }
}
